<template>
  <div class="row mb-3" v-if="$hasRole(role, ['Administrator'])">
    <div class="col">
      <button
        class="btn btn-primary"
        type="button"
        @click="$router.push({ name: 'WorkerEdit', params: { workerId: 'new' } })"
      >
        Nov delavec
      </button>
    </div>
  </div>

  <div class="row" v-show="$hasRoleStartsWith(role, ['Leader', 'Admin'])">
    <div class="col-sm-4">
      <label class="form-label" for="is-active">Prikaži aktivne</label>
      <div class="form-check form-switch">
        <input class="form-check-input" type="checkbox" id="is-active" v-model="filters.active" />
      </div>
    </div>
    <div class="col-sm-4">
      <label class="form-label" for="is-absent">Prikaži odsotne</label>
      <div class="form-check form-switch">
        <input class="form-check-input" type="checkbox" id="is-absent" v-model="filters.absent" />
      </div>
    </div>
    <div class="col-sm-4">
      <label class="form-label" for="show-orders">Prikaži SDN</label>
      <div class="form-check form-switch">
        <input class="form-check-input" type="checkbox" id="show-orders" v-model="filters.showOrders" />
      </div>
    </div>
  </div>

  <div class="row" v-if="$hasRoleStartsWith(role, ['Leader', 'Admin']) && filters">
    <div class="col">
      <div class="table-responsive">
        <table class="table table-stripped table-sm table-hover">
          <thead>
            <tr>
              <th scope="col">Delavec</th>
              <th scope="col">Avto</th>
              <th scope="col">Domače skladišče</th>
              <th scope="col">Dom</th>
              <th scope="col">Preskus uhajanja</th>
              <th scope="col">Podpis</th>
              <th scope="col">Na pavzi od</th>
            </tr>
          </thead>
          <tbody>
            <tr
              role="button"
              v-for="w in flattedWorkers.filter((w) => w.isActive === filters.active && w.isAbsent === filters.absent)"
              :key="w.isServiceOrder ? w.serviceOrderId : w.id"
              :class="{ 'bg-warning': w.isAbsent, 'worker-row': !w.isServiceOrder }"
            >
              <td :rowspan="w.rowSpan" v-if="!w.isServiceOrder" @click="select(w)" class="fw-semibold">
                {{ w.fullName }} <span class="badge bg-primary" v-show="w.isAbsent">Odsoten</span>
              </td>
              <td v-if="!w.isServiceOrder" @click="select(w)">
                <span
                  v-if="w.vehicleName"
                  :class="w.hasTracker ? 'text-success' : 'text-danger'"
                  :title="w.hasTracker ? 'GNSS sledilnik' : 'Avto nima veljavne številke GNSS sledilnika'"
                  >{{ w.vehicleName }}</span
                ><span v-else class="text-danger fw-semibold">manjka</span>
              </td>
              <td v-if="!w.isServiceOrder" @click="select(w)">
                {{ w.homeWarehouseName }}<span v-if="!w.homeWarehouseName" class="text-danger fw-semibold">manjka</span>
              </td>
              <td v-if="!w.isServiceOrder" @click="select(w)">
                <span v-if="w.hasHomeLocation">DA</span><span v-else class="text-danger fw-semibold">manjka</span>
              </td>
              <td v-if="!w.isServiceOrder" @click="select(w)">{{ w.canSignLeakageForm ? "DA" : "" }}</td>
              <td v-if="!w.isServiceOrder" @click="select(w)">{{ w.hasBase64Signature ? "DA" : "" }}</td>
              <td v-if="!w.isServiceOrder" @click="select(w)">
                <span v-if="!!w.workPauseStartDateTime">{{
                  $dayjs(w.workPauseStartDateTime).format("ddd HH:mm")
                }}</span>
              </td>
              <td v-if="w.isServiceOrder" @click="select(w)" class="smt ps-3">↳ {{ w.orderNumber }}</td>
              <td v-if="w.isServiceOrder" @click="select(w)" class="smt">{{ w.status }}</td>
              <td v-if="w.isServiceOrder" @click="select(w)" class="smt">{{ w.facilityName }}</td>
              <td v-if="w.isServiceOrder" @click="select(w)" colspan="3" class="smt">{{ w.subject }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
  <div v-else class="alert alert-warning" role="alert">
    <p>Seznam dostopen le administratorjem</p>
    <hr />
    <p class="mb-0">
      <a role="button" @click="select({ id: currentWorkerId })" class="alert-link">Poglej lastne podatke</a>.
    </p>
  </div>
</template>

<script setup>
import { ref, toRef, toRefs, computed, defineComponent, watch, reactive, onMounted, inject } from "vue"
import { useAuth, authState, isAuthenticated, user, role, currentWorkerId } from "../composition-api/useAuth"
import { listEntitiesAsync } from "../api"
import { useEnums } from "../composition-api/useEnums"

defineOptions({ name: "Workers" })

const route = VueRouter.useRoute()
const router = VueRouter.useRouter()

const { fetchAuthAsync } = useAuth()

const workers = ref([])

const DEFAULT_FILTERS = () => {
  return { role: "Worker", active: true, absent: false, showOrders: false }
}

const filters = ref(DEFAULT_FILTERS())

const { roles, loadEnumsAsync } = useEnums()

const flattedWorkers = computed(() =>
  workers.value.flatMap((w) => {
    const result = []

    result.push({
      id: w.id,
      fullName: w.fullName,
      vehicleName: w.vehicleName,
      hasTracker: w.hasTracker,
      homeWarehouseName: w.homeWarehouseName,
      hasHomeLocation: w.hasHomeLocation,
      isSetupComplete: w.isSetupComplete,
      canSignLeakageForm: w.canSignLeakageForm,
      hasBase64Signature: w.hasBase64Signature,
      workPauseStartDateTime: w.workPauseStartDateTime,
      isActive: w.isActive,
      isAbsent: w.isAbsent,
      isServiceOrder: false,
      // the name cell spans the worker's order rows, so only count them when they are shown
      rowSpan: (filters.value.showOrders ? w.serviceOrders?.length || 0 : 0) + 1,
    })
    if (filters.value.showOrders && w.serviceOrders?.length) {
      result.push(
        ...w.serviceOrders.map((so) => {
          return {
            fullName: w.fullName,
            canSignLeakageForm: w.canSignLeakageForm,
            hasBase64Signature: w.hasBase64Signature,
            workPauseStartDateTime: w.workPauseStartDateTime,
            isActive: w.isActive,
            isAbsent: w.isAbsent,
            isServiceOrder: true,
            ...so,
          }
        }),
      )
    }
    return result
  }),
)

const select = (s) => {
  if (s.isServiceOrder) router.push({ name: "ServiceOrder", params: { workOrderId: s.serviceOrderId } })
  else router.push({ name: "WorkerEdit", params: { workerId: s.id } })
}

onMounted(async () => {
  await loadEnumsAsync()
  workers.value = await listEntitiesAsync(fetchAuthAsync, "Workers")
})
</script>

<style scoped>
/* a clear line where each worker's group (worker row + their orders) starts */
.worker-row > td {
  border-top: 2px solid var(--bs-gray-500);
}
</style>
